import { useDebounce } from "@src/appV2/lib/utils/useDebounce";
import {
  calculateEstimatedDrivingDistanceInMiles,
  calculateGeoDistanceInMiles,
} from "@src/appV2/Location";
import { type GeoLocation } from "@src/appV2/Location/types";
import { useEffect, useMemo, useState } from "react";

import { useGeoLocationFromUrlParams } from "../../GeoLocation/useGeoLocationFromUrlParams";
import { formatDistance, useDistanceToWorkplace } from "../../Workplace/useDistanceToWorkplace";
import { DEFAULT_DISTANCE_IN_MILES_FILTER } from "../Filters/constants";
import type { MapViewWorkplace } from "../MapView/types";
import { useMapViewWorkplacesData } from "../MapView/useMapViewWorkplacesData";
import { type WorkplaceListItemProps } from "./Item";
import type { WorkplaceResultGroup } from "./List";
import { useOtherWorkplaces } from "./useOtherWorkplaces";

const MAX_DISTANCE_TO_FETCH = 1000;
const DISTANCE_TO_FETCH_INCREMENT = DEFAULT_DISTANCE_IN_MILES_FILTER;

function sortWorkplacesByDistance(workplaces: MapViewWorkplace[], geoLocationFilter: GeoLocation) {
  return workplaces.sort((a, b) => {
    const distanceA = calculateGeoDistanceInMiles(geoLocationFilter, a.attributes.location);
    const distanceB = calculateGeoDistanceInMiles(geoLocationFilter, b.attributes.location);
    return distanceA - distanceB;
  });
}

function useMaxDistanceFetched(
  geoLocationFilter: GeoLocation,
  distanceFilter: number,
  furthestWorkplace?: MapViewWorkplace
) {
  const { distanceInMiles: furthestWorkplaceDistance } = useDistanceToWorkplace({
    workplaceGeoLocation: furthestWorkplace?.attributes.location,
    workerGeoLocation: geoLocationFilter,
  });

  // round to the nearest 50 miles
  const roundedDistance = Math.round(Math.ceil(furthestWorkplaceDistance ?? 0) / 50) * 50;

  return roundedDistance > distanceFilter ? roundedDistance : distanceFilter;
}

function mapViewWorkplaceToListItemProps(
  workplace: MapViewWorkplace,
  geoLocation: GeoLocation
): WorkplaceListItemProps {
  const drivingDistance = calculateEstimatedDrivingDistanceInMiles(
    geoLocation,
    workplace.attributes.location
  );
  const formattedDistance = formatDistance(drivingDistance);

  return {
    workplaceId: workplace.id,
    name: workplace.attributes.name,
    formattedDistance,
    shiftsCount: workplace.shiftsCount,
    showDividerAfter: false,
  };
}

export function useWorkplaceList() {
  const { geoLocation: geoLocationFromUrlParams } = useGeoLocationFromUrlParams();

  const [searchTerm, setSearchTerm] = useState("");

  const debouncedSearchTerm = useDebounce({
    value: searchTerm,
    debounceTimeInMs: 500,
  });

  const {
    data: {
      mapViewWorkplaces: workplacesMatchingFilters,
      distanceFilter,
      geoLocationFilter: latestGeoLocationFilter,
    },
    isLoading: isLoadingMatchingWorkplaces,
    isSuccess: isMatchingWorkplacesSuccess,
    isError: isMatchingWorkplacesError,
    fetchMore,
  } = useMapViewWorkplacesData({
    geoLocation: geoLocationFromUrlParams,
    shouldFetchUrgentShifts: false,
  });

  const geoLocationFilter = geoLocationFromUrlParams ?? latestGeoLocationFilter;

  const [isFetchingMoreMatchingWorkplaces, setIsFetchingMoreMatchingWorkplaces] = useState(false);

  // Fetch more workplaces increasing the distance radius
  // until workplaces are found or the max distance is reached.
  useEffect(() => {
    if (
      !isMatchingWorkplacesSuccess ||
      workplacesMatchingFilters.length > 0 ||
      distanceFilter >= MAX_DISTANCE_TO_FETCH
    ) {
      setIsFetchingMoreMatchingWorkplaces(false);
      return;
    }

    setIsFetchingMoreMatchingWorkplaces(true);
    fetchMore(geoLocationFilter, distanceFilter + DISTANCE_TO_FETCH_INCREMENT);
  }, [
    workplacesMatchingFilters,
    distanceFilter,
    geoLocationFilter,
    fetchMore,
    isMatchingWorkplacesSuccess,
  ]);

  const sortedWorkplacesWithShiftsMatchingFilters = sortWorkplacesByDistance(
    workplacesMatchingFilters,
    geoLocationFilter
  );

  const maxDistanceFetched = useMaxDistanceFetched(
    geoLocationFilter,
    distanceFilter,
    sortedWorkplacesWithShiftsMatchingFilters.at(-1)
  );

  const workplacesWithShiftsMatchingFilters =
    debouncedSearchTerm === ""
      ? sortedWorkplacesWithShiftsMatchingFilters
      : sortedWorkplacesWithShiftsMatchingFilters.filter((workplace) =>
          workplace.attributes.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
        );

  const {
    data: allWorkplacesData,
    isLoading: isAllWorkplacesLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useOtherWorkplaces({
    filter: {
      query: debouncedSearchTerm,
      location: geoLocationFilter,
    },
  });

  const isLoadingWorkplaces =
    isLoadingMatchingWorkplaces || isFetchingMoreMatchingWorkplaces || isAllWorkplacesLoading;

  const matchingWorkplaceIds = useMemo(
    () => new Set(workplacesWithShiftsMatchingFilters.map((workplace) => workplace.id)),
    [workplacesWithShiftsMatchingFilters]
  );

  // Convert allWorkplaces data to WorkplaceListItemProps, excluding duplicates
  const filteredAllWorkplacesAsListItems: WorkplaceListItemProps[] = useMemo(
    () =>
      allWorkplacesData?.pages
        ?.flatMap((page) => page.data)
        .filter((workplace) => !matchingWorkplaceIds.has(workplace.id))
        .map((workplace) => {
          const distance = workplace.attributes?.distanceFromWorker ?? 0;

          return {
            workplaceId: workplace.id,
            name: workplace.attributes?.name ?? "",
            formattedDistance: formatDistance(distance),
            shiftsCount: workplace.attributes?.openShiftCount ?? 0,
          };
        }) ?? [],
    [allWorkplacesData?.pages, matchingWorkplaceIds]
  );

  const matchingWorkplacesAsListItems: WorkplaceListItemProps[] = useMemo(
    () =>
      workplacesWithShiftsMatchingFilters.map((workplace) =>
        mapViewWorkplaceToListItemProps(workplace, geoLocationFilter)
      ),
    [workplacesWithShiftsMatchingFilters, geoLocationFilter]
  );

  const matchingWorkplacesGroup: WorkplaceResultGroup = {
    workplaces: matchingWorkplacesAsListItems,
    totalCount: workplacesWithShiftsMatchingFilters.length,
  };

  const totalCount = allWorkplacesData?.pages?.[0]?.count ?? 0;

  const allOtherWorkplacesGroup: WorkplaceResultGroup = {
    workplaces: filteredAllWorkplacesAsListItems,
    totalCount,
  };

  const handleLoadMore = async () => {
    if (hasNextPage) {
      await fetchNextPage();
    }
  };

  return {
    searchTerm,
    setSearchTerm,
    debouncedSearchTerm,
    isLoadingWorkplaces,
    isMatchingWorkplacesError,
    isMatchingWorkplacesSuccess,
    maxDistanceFetched,
    matchingWorkplacesGroup,
    allOtherWorkplacesGroup,
    isFetchingNextPage,
    hasNextPage,
    handleLoadMore,
  };
}
